import React, { useEffect, useState, useCallback } from "react";
import apiClient from "../../api/apiClient";
import { formatWeightFromGrams, toGrams } from "../../utils/weightUtils";
import { useConfirm } from "../../context/confirmContext";
import "./TransferRequestsModal.css";

const WEIGHT_UNITS = ["g", "oz", "lb"];

function parseFormattedWeight(formatted) {
    if (!formatted) return { value: "", unit: "lb" };
    const parts = String(formatted).trim().split(" ");
    return {
        value: parts[0] || "",
        unit: parts[1] || "lb",
    };
}

const TransferRequestsModal = ({ licenseNo, onClose, onCountChange }) => {
    const confirm = useConfirm(); 
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState("");
    const [editWeights, setEditWeights] = useState({});
    const [rowState, setRowState] = useState({});

    const fetchRequests = useCallback(async () => {
        if (!licenseNo) return;
        setLoading(true);
        setFetchError("");
        try {
            const res = await apiClient.get(`/transfer-requests?licenseNo=${licenseNo}&status=requested`);
            const data = Array.isArray(res.data?.requests) ? res.data.requests : [];
            setRequests(data);
            const initialWeights = {};
            data.forEach((r) => {
                const parsed = parseFormattedWeight(formatWeightFromGrams(parseFloat(r.weight_requested_grams)));
                initialWeights[r.id] = { value: parsed.value, unit: parsed.unit };
            });
            setEditWeights(initialWeights);
            setRowState({});
            if (onCountChange) onCountChange(data.length);
        } catch {
            setFetchError("Could not load requests. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [licenseNo, onCountChange]);

    useEffect(() => { fetchRequests(); }, [fetchRequests]);

    const updateRowState = useCallback((id, patch) => {
        setRowState((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    }, []);

    const handleAccept = async (requestId) => {
        const ok = await confirm({
            title: "Approve Transfer",
            message: `Approve this marketplace transfer request?`,
            confirmLabel: "Approve",
            variant: "primary",
        });
        if (!ok) return;
        const weightData = editWeights[requestId] || { value: "", unit: "lb" };
        const weightNum = parseFloat(weightData.value);
        if (isNaN(weightNum) || weightNum <= 0) {
            updateRowState(requestId, { error: "Enter a valid weight." });
            return;
        }
        const weightGrams = toGrams(weightNum, weightData.unit);
        updateRowState(requestId, { submitting: true, error: "" });
        try {
            await apiClient.patch(`/transfer-requests/${requestId}/accept`, { weight_grams: weightGrams });
            updateRowState(requestId, { submitting: false, done: "accepted" });
            const res = await apiClient.get(`/transfer-requests?licenseNo=${licenseNo}&status=requested`);
            const remaining = Array.isArray(res.data?.requests) ? res.data.requests : [];
            if (onCountChange) onCountChange(remaining.length);
        } catch (err) {
            updateRowState(requestId, {
                submitting: false,
                error: err.response?.data?.error || "Accept failed. Please try again.",
            });
        }
    };

    const handleDeny = async (requestId) => {
        const ok = await confirm({
            title: "Deny Transfer",
            message: "Deny this marketplace transfer request? This cannot be undone.",
            confirmLabel: "Deny",
            variant: "danger",
        });
        if (!ok) return;
        updateRowState(requestId, { submitting: true, error: "" });
        try {
            await apiClient.patch(`/transfer-requests/${requestId}/deny`);
            updateRowState(requestId, { submitting: false, done: "denied" });
            const res = await apiClient.get(`/transfer-requests?licenseNo=${licenseNo}&status=requested`);
            const remaining = Array.isArray(res.data?.requests) ? res.data.requests : [];
            if (onCountChange) onCountChange(remaining.length);
        } catch (err) {
            updateRowState(requestId, {
                submitting: false,
                error: err.response?.data?.error || "Deny failed. Please try again.",
            });
        }
    };

    const renderRequestItem = (req) => {
        const row = rowState[req.id] || {};
        const isDone = !!row.done;
        const weightData = editWeights[req.id] || { value: "", unit: "lb" };

        return (
            <li key={req.id} className={`trm__item${isDone ? " trm__item--done" : ""}`}>
                <div className="trm__item-meta">
                    <span className="trm__product-name">{req.product_name}</span>
                    {req.requester_company && (
                        <span className="trm__requester-company">
                            {req.requester_company}
                            {req.requester_location_name && (
                                <span className="trm__requester-location">
                                    {" — "}{req.requester_location_name}
                                </span>
                            )}
                        </span>
                    )}
                    <span className="trm__requester">{req.requester_email}</span>
                    <span className="trm__requested-weight">
                        Requested: {formatWeightFromGrams(parseFloat(req.weight_requested_grams))}
                    </span>
                </div>

                {isDone ? (
                    <span className={`trm__resolved trm__resolved--${row.done}`}>
                        {row.done === "accepted" ? "✓ Approved" : "✕ Denied"}
                    </span>
                ) : (
                    <div className="trm__item-controls">
                        <div className="trm__weight-row">
                            <input
                                type="number"
                                className="trm__weight-input"
                                value={weightData.value}
                                onChange={(e) =>
                                    setEditWeights((prev) => ({
                                        ...prev,
                                        [req.id]: { ...prev[req.id], value: e.target.value },
                                    }))
                                }
                                placeholder="0.00"
                                min="0.01"
                                step="0.01"
                                disabled={row.submitting}
                            />
                            <select
                                className="trm__weight-unit"
                                value={weightData.unit}
                                onChange={(e) =>
                                    setEditWeights((prev) => ({
                                        ...prev,
                                        [req.id]: { ...prev[req.id], unit: e.target.value },
                                    }))
                                }
                                disabled={row.submitting}
                            >
                                {WEIGHT_UNITS.map((u) => (
                                    <option key={u} value={u}>{u}</option>
                                ))}
                            </select>
                        </div>
                        {row.error && <p className="trm__row-error">{row.error}</p>}
                        <div className="trm__actions">
                            <button
                                className="trm__btn trm__btn--accept"
                                onClick={() => handleAccept(req.id)}
                                disabled={row.submitting}
                            >
                                {row.submitting ? "…" : "Approve"}
                            </button>
                            <button
                                className="trm__btn trm__btn--deny"
                                onClick={() => handleDeny(req.id)}
                                disabled={row.submitting}
                            >
                                Deny
                            </button>
                        </div>
                    </div>
                )}
            </li>
        );
    };

    return (
        <div className="tw-modal-overlay" onClick={onClose}>
            <div className="tw-modal trm__modal" onClick={(e) => e.stopPropagation()}>
                <div className="tw-modal__header">
                    <h3 className="tw-modal__title">Marketplace Requests</h3>
                    <button className="tw-modal__close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="tw-modal__body" data-tutorial="trm-actions">
                    {loading && <p className="trm__state-msg">Loading requests…</p>}
                    {!loading && fetchError && (
                        <div className="trm__error-banner">
                            <p>{fetchError}</p>
                            <button className="trm__retry-btn" onClick={fetchRequests}>Retry</button>
                        </div>
                    )}
                    {!loading && !fetchError && requests.length === 0 && (
                        <p className="trm__state-msg trm__state-msg--empty">No pending marketplace requests.</p>
                    )}
                    {!loading && !fetchError && requests.length > 0 && (
                        <ul className="trm__list">
                            {requests.map((req) => renderRequestItem(req))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TransferRequestsModal;
